import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Inject, Input, PLATFORM_ID, Renderer2, ViewChild } from '@angular/core';
import { AddonFacebookChatScript, AddonGoogleTagManagerScript, AddonLanguageTranslationService } from '@domains/frontend/page-addons';
import { Util } from '@libs/utilities/util';
import { EnumLanguage } from '@libs/constants';
import { Router } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';

import { LegalCookiesBannerStorageHandler } from '../../services/cookies-banner/cookies-storage/cookies-storage.handler';
import { EnumLegalCookiesBannerInjectionToken, ILegalCookiesBannerModuleConfig } from './cookies-banner.interface';
import { ILegalCookieBannerData, LEGAL_COOKIE_BANNER_DATA } from './cookies-banner.data';


@Component({
    selector: 'legal-cookies-banner',
    templateUrl: './cookies-banner.component.html',
    styleUrls: ['./cookies-banner.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class LegalCookiesBannerComponent implements AfterViewInit {

	//** Query dialog element */
	@ViewChild('dialog') dialogElementRef!: ElementRef<HTMLDialogElement>;

	//** Input decorator */
	@Input() isMultiLingual: boolean = false;

	//** Helper Variables */
	showCookiesSettings	: boolean = false;
	socialMediaCookies	: boolean = false;
	analyticsCookies	: boolean = false;
	cookieBannerData 	: ILegalCookieBannerData;
	selectedLanguage 	: EnumLanguage;

	//** Enums */
	readonly EnumLanguage : typeof EnumLanguage = EnumLanguage;


	constructor(
		@Inject(PLATFORM_ID)
		private platform_id: object,
		@Inject(EnumLegalCookiesBannerInjectionToken.Config)
		private cookiesModuleConfig	 	: ILegalCookiesBannerModuleConfig,
		private renderer2			 	: Renderer2,
		private cookiesStorageHandler	: LegalCookiesBannerStorageHandler,
		private facebookChatScript	 	: AddonFacebookChatScript,
		private googleTagManagerScript	: AddonGoogleTagManagerScript,
		public translationService		: AddonLanguageTranslationService,
		private cdr						: ChangeDetectorRef,
		private elementRef 				: ElementRef,
		private router 					: Router
	) {}

	ngAfterViewInit(): void {

		//0 - are we in the browser?
		// > Note: only if we are in the browser we can access the cookies.
		// > If we are not in the browser and if we are the prerendering or server
		// > nodeJS process and don't have access to the window or document objects.
		if (!isPlatformBrowser(this.platform_id)) return;

		//1 - setup social and analytical services
		this.setupSocialServices();
		this.setupAnalyticalServices();

		//2 - check if cookie settings are set in local storage else show cookie banner
		if (this.cookiesStorageHandler.hasAcceptedAll() || this.cookiesStorageHandler.hasSetToday()) return;

		//3 - query for dialog element and open it
		if (!this.dialogElementRef) return;
		this.dialogElementRef.nativeElement.showModal();

		//4 - disable closing of modal on esc key
		this.renderer2.listen(this.dialogElementRef.nativeElement, 'cancel', (event: Event) => {
			event.preventDefault();
		});

		//5 - get the data by language
		this.updatePageContentOnLanguageChange();

		//6 - redirect the links programmatically
		this.handleBannerButtons();
	}

	//** Accepts all cookie options */
	acceptAll(): void {

		//0 - enable all optional cookies
		this.cookiesStorageHandler.setAnalytics(true);
		this.cookiesStorageHandler.setSocialMedia(true);

		//1 - dismiss banner
		this.closeBanner();

		//2 - setup services
		this.setupSocialServices();
		this.setupAnalyticalServices();
	}

	//** Denies all cookie options */
	denyAll(): void {

		//0 - disable all optional cookies
		this.cookiesStorageHandler.setAnalytics(false);
		this.cookiesStorageHandler.setSocialMedia(false);

		//1 - dismiss banner
		this.closeBanner();
	}

	//** Save social media cookies option on change */
	saveSocialMediaOptions(allowSocialMediaCookies: boolean): void {
		this.socialMediaCookies = allowSocialMediaCookies;
	}

	//** Save analytical cookies option on change */
	saveAnalyticsOptions(allowAnalyticalCookies: boolean): void {
		this.analyticsCookies = allowAnalyticalCookies;
	}

	//** Save changes */
	saveChanges(): void {

		//0 - save cookies
		this.cookiesStorageHandler.setSocialMedia(this.socialMediaCookies);
		this.cookiesStorageHandler.setAnalytics(this.analyticsCookies);

		//1 - close banner
		this.closeBanner();
	}

	//** Closes the banner */
	private closeBanner(): void {
		this.dialogElementRef.nativeElement.close();
	}

	//** Setup services */
	private setupSocialServices(): void {

		//0 - do not add scripts if social media cookies is disabled
		const isSocialMediaCookiesEnabled: boolean = this.cookiesStorageHandler.hasAcceptedSocialMedia();
		if (!isSocialMediaCookiesEnabled) return;

		//1 - create facebook chat
		const isChatIdSet: boolean = Boolean(this.cookiesModuleConfig.social.facebookConfig.pageId);
		if (isChatIdSet) this.facebookChatScript.create();
	}

	private setupAnalyticalServices(): void {

		//0 - do not add scripts if analytics cookies is disabled
		const isAnalyticsCookiesEnabled: boolean = this.cookiesStorageHandler.hasAcceptedAnalytics();
		if (!isAnalyticsCookiesEnabled) return;

		//1 - create google tag manager
		const isGtmSet: boolean = Boolean(this.cookiesModuleConfig.analytics.googleTagId);
		if (isGtmSet) this.googleTagManagerScript.create();
	}


	//** Handle banner button actions */
	/**------------------------------------------------------
	 * Note:
	 * This is related to banner info content. Please have
	 * a look at the data.ts file
	 */
	private handleBannerButtons(): void {
		const dynamicContentButtons: HTMLAnchorElement[] = this.elementRef.nativeElement.querySelectorAll('.dynamic-link-btn');
		for (const button of dynamicContentButtons) {
			this.renderer2.listen(button, 'click', (event: Event) => {
				//0 - prevent the default functionality
				event.preventDefault();

				//1 - hide the banner and detect changes to refresh changes
				this.closeBanner();

				//2 - redirect the page to router link
				this.router.navigateByUrl(button.getAttribute('routerLink')!);
			});
		}
	}

	//** Update content by language */
	private updatePageContentOnLanguageChange(): void {
		this.translationService.getTranslationObservable().subscribe((language: EnumLanguage) => {

			//0 - is valid language
			if (!Util.Enum.isValid(EnumLanguage, language)) throw new Error(`LegalCookiesBannerComponent => ngAfterViewInit => FATAL ERROR: selectedLanguage is not valid : ${language}`);

			//1 - get the data
			this.selectedLanguage = language;
			this.cookieBannerData = LEGAL_COOKIE_BANNER_DATA[language]!;
			this.cdr.detectChanges();
		});
	}
}
